import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable7 = ({ data }) => {
  const pdfRef = useRef();

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  // ---- Helper: per-age-group average across years (for table) ----
  const averages = data.map((ageGroup) => {
    const total = years.reduce(
      (sum, year) => sum + Number(ageGroup[`year_${year}`]),
      0,
    );
    const average = total / years.length;
    return { agegrp_label: ageGroup.agegrp_label, average };
  });

  // ---- Helper: overall average across age groups for each year (for chart) ----
  const avgPerYear = years.map((year) => {
    const total = data.reduce(
      (sum, group) => sum + Number(group[`year_${year}`]),
      0,
    );
    return total / data.length;
  });

  // ---- Summary metrics ----
  const totalsPerYear = years.map((year) =>
    data.reduce((sum, group) => sum + Number(group[`year_${year}`]), 0),
  );

  const peakTotal = Math.max(...totalsPerYear);
  const peakYear = years[totalsPerYear.indexOf(peakTotal)];

  const growthPercent = (
    ((totalsPerYear[totalsPerYear.length - 1] - totalsPerYear[0]) /
      totalsPerYear[0]) *
    100
  ).toFixed(1);

  const topAvgGroup = averages.reduce((max, row) =>
    row.average > max.average ? row : max,
  );

  // ---- Color palette (fixed, no random colors) ----
  const palette = [
    "#FFA1B5",
    "#86C7F3",
    "#FFE29A",
    "#93D9D9",
    "#C2A3FF",
    "#FFC58C",
    "#B9D2AA",
    "#6794D1",
  ];

  // ---- Chart datasets ----
  const datasets = data.map((ageGroup, idx) => ({
    label: ageGroup.agegrp_label,
    data: years.map((year) => Number(ageGroup[`year_${year}`])),
    borderColor: palette[idx % palette.length],
    backgroundColor: palette[idx % palette.length] + "30",
    borderWidth: 3,
    tension: 0.4,
    pointBackgroundColor: "white",
    pointBorderColor: palette[idx % palette.length],
    pointBorderWidth: 2,
    pointRadius: 5,
    pointHoverRadius: 7,
    fill: true,
  }));

  // Average across age groups per year
  datasets.push({
    label: "Average across Age Groups",
    data: avgPerYear,
    borderColor: "#000000",
    backgroundColor: "rgba(0,0,0,0.3)",
    borderWidth: 2,
    tension: 0.4,
    pointBackgroundColor: "black",
    pointBorderColor: "black",
    pointBorderWidth: 2,
    pointRadius: 5,
    pointHoverRadius: 7,
    fill: false,
  });

  const chartData = {
    labels: years,
    datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 14 } },
      },
      title: {
        display: true,
        text: "📈 Yearly Employment by Age Group with Averages",
        font: { size: 16, weight: "bold" },
      },
    },
    elements: {
      line: {
        borderJoinStyle: "round",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
        },
      },
    },
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("employment_agegroup_with_averages.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Employment by Age Group with Averages (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Workforce Growth
            </p>
            <p className="text-2xl font-bold text-blue-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Workforce Year
            </p>
            <p className="text-2xl font-bold text-yellow-800">{peakYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Average Age Group
            </p>
            <p className="text-xl font-bold text-green-800">
              {topAvgGroup.agegrp_label}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year}
                    </th>
                  ))}
                  <th className="p-2">Average</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr
                    key={row.agegrp_label}
                    className={`${
                      averages[index].agegrp_label === topAvgGroup.agegrp_label
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.agegrp_label}</td>
                    {years.map((year) => (
                      <td key={year} className="p-2">
                        {Number(row[`year_${year}`]).toLocaleString("en-US")}
                      </td>
                    ))}
                    <td className="p-2">
                      {averages[index].average.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 p-2 px-4 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="Download icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable7;
